/**
 * Authentication Orchestrator - Singleton Wrapper for Refactored Version
 *
 * This file serves as a backward-compatible wrapper for the refactored
 * authentication orchestrator. The original 2,500+ line monolithic file
 * has been broken down into modular, maintainable composables.
 *
 * IMPORTANT: This now implements a singleton pattern to ensure all components
 * share the same orchestrator instance for proper state synchronization.
 */

import { useAuthOrchestratorRefactored } from './auth/useAuthOrchestratorRefactored';

// Re-export types for backward compatibility
// These types are originally from @/types/profileTypes but were re-exported
// by the old orchestrator, so we maintain that pattern
export type {
  AuthPhase,
  GeneralProfileData,
  TeacherProfileData,
  StudentProfileData,
  ParentProfileData,
  EmployerProfileData,
  SchoolProfileData,
  OrganizerProfileData
} from '@/types/profileTypes';

// Also export the orchestrator state interface if needed
export type { AuthOrchestratorState } from './auth/modules/useAuthState';

// Singleton instance to share across all components
let orchestratorInstance: ReturnType<typeof useAuthOrchestratorRefactored> | null = null;

/**
 * Main orchestrator composable - now using a singleton pattern
 *
 * This ensures that all components share the same orchestrator instance,
 * which is critical for state synchronization between the dialog component
 * and the components that trigger auth actions.
 *
 * @returns The complete auth orchestrator API with all methods and state
 */
export function useAuthOrchestrator() {
  // Create instance only once
  if (!orchestratorInstance) {
    orchestratorInstance = useAuthOrchestratorRefactored();
  }

  return orchestratorInstance;
}

/**
 * For debugging or gradual migration, you can access the old implementation
 * by importing from './useAuthOrchestrator.old'
 *
 * The refactored version is split into these modules:
 * - useAuthState: Centralized state management (~150 lines)
 * - useAuthRecovery: Session recovery (~200 lines)
 * - useAuthNavigation: Phase navigation (~300 lines)
 * - useAuthFlowControl: Flow control (~350 lines)
 * - useAuthCore: Core auth functions (~250 lines)
 * - useAuthProfile: Profile management (~400 lines)
 * - useAuthIdentity: Identity linking (~350 lines)
 * - useAuthAlternateContact: Alternate contact (~200 lines)
 */
